import React, { useState } from 'react';
import styled from 'styled-components';
import { FaTelegram, FaCheckCircle, FaExternalLinkAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 0.75rem;
  background: ${props => props.theme.colors.gradient1};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const PageDescription = styled.p`
  max-width: 800px;
  font-size: 1.1rem;
  opacity: 0.9;
`;

const CategorySection = styled.div`
  background: ${props => props.theme.colors.darkBg2};
  border-radius: 12px;
  transition: ${props => props.theme.transition.default};
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow: hidden;
  box-shadow: ${props => props.isOpen ? props.theme.boxShadow.medium : props.theme.boxShadow.small};
  
  &:hover {
    border-color: rgba(108, 92, 231, 0.2);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  cursor: pointer;
  user-select: none;
  transition: ${props => props.theme.transition.default};
  
  &:hover {
    background: rgba(108, 92, 231, 0.08);
  }
`;

const CategoryIcon = styled.div`
  font-size: 1.6rem;
  margin-right: 0.75rem;
  color: ${props => props.theme.colors.primaryLight};
`;

const CategoryTitle = styled.h2`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.white};
  margin: 0;
`;

const CategoryQuota = styled.span`
  margin-left: auto;
  background: rgba(108, 92, 231, 0.2);
  color: ${props => props.theme.colors.primaryLight};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-right: 0.75rem;
`;

const ChevronIcon = styled.div`
  color: ${props => props.theme.colors.primaryLight};
  font-size: 1rem;
  transition: transform 0.3s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

const CategoryContent = styled.div`
  padding: ${props => props.isOpen ? '1rem 1.25rem' : '0 1.25rem'};
  max-height: ${props => props.isOpen ? '800px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: ${props => props.isOpen ? '1' : '0'};
  border-top: ${props => props.isOpen ? '1px solid rgba(255, 255, 255, 0.05)' : 'none'};
`;

const SellersList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const SellerCard = styled.div`
  background: ${props => props.theme.colors.darkBg3};
  border-radius: 10px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.03);
  transition: ${props => props.theme.transition.default};
  
  &:hover {
    transform: translateY(-2px);
    background: rgba(108, 92, 231, 0.1);
    border-color: rgba(108, 92, 231, 0.2);
  }
`;

const SellerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SellerName = styled.h3`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
`;

const VerifiedBadge = styled(FaCheckCircle)`
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
`;

const SellerLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TelegramLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text};
  transition: ${props => props.theme.transition.default};
  
  &:hover {
    background: rgba(108, 92, 231, 0.2);
    color: ${props => props.theme.colors.white};
  }
  
  svg {
    color: #0088cc;
  }
`;

const SearchBox = styled.div`
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: ${props => props.theme.colors.darkBg3};
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    outline: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textMuted};
  }
`;

const CertifiedSellers = () => {
  const [openCategories, setOpenCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  const categories = [
    {
      name: "AWS CRACKER",
      icon: "🌐",
      quota: "2/2",
      sellers: [
        { name: "coldcracker", telegramUser: "@coldcrackerpms", telegramChannel: "coldcracker.co" },
        { name: "ErrorC1", telegramUser: "@errorc1", telegramChannel: "@GlitchCracker" }
      ]
    },
    {
      name: "AWS & SMTP",
      icon: "🔩",
      quota: "1/1",
      sellers: [
        { name: "Toma", telegramUser: "@tomaMA212", telegramChannel: "https://t.me/tomashopcanal" }
      ]
    },
    {
      name: "SCAMA(S)",
      icon: "📋",
      quota: "1/1",
      sellers: [
        { name: "SairocDev", telegramUser: "@SairocDev_bot", telegramChannel: "https://t.me/+wVof9-gjen4zOTg8" }
      ]
    },
    {
      name: "AUTOSHOP BUILDER",
      icon: "🛒",
      quota: "1/1",
      sellers: [
        { name: "ZenBot", telegramUser: "@zenbot_canal", telegramChannel: "zenbot.dev" }
      ]
    },
    {
      name: "SPOOFER",
      icon: "🏦",
      quota: "1/1",
      sellers: [
        { name: "Lite", telegramUser: "@Lite_Help", telegramChannel: "@LiteSpoofer_Bot" }
      ]
    },
    {
      name: "SENDER ID MAIL",
      icon: "📨",
      quota: "1/1",
      sellers: [
        { name: "Hroselmack", telegramUser: "@Hroselmack75", telegramChannel: "@priv8botbot_bot" }
      ]
    },
    {
      name: "NL & ML",
      icon: "📑",
      quota: "1/1",
      sellers: [
        { name: "SociOld", telegramUser: "@SociOld", telegramChannel: "@DeepLeadsBot" }
      ]
    },
    {
      name: "FAUSSAIRES",
      icon: "📇",
      quota: "0/1",
      sellers: []
    },
    {
      name: "REFUND",
      icon: "♻️",
      quota: "1/1",
      sellers: [
        { name: "Zeno", telegramUser: "@rfzenoo", telegramChannel: "@EasyXRP" }
      ]
    },
    {
      name: "PACK ID",
      icon: "🆔",
      quota: "1/2",
      sellers: [
        { name: "Rapidox", telegramUser: "@Rapidoxsecours2", telegramChannel: null }
      ]
    },
    {
      name: "NDD PLESK VPS",
      icon: "⚙️",
      quota: "2/2",
      sellers: [
        { name: "MenaceQP", telegramUser: "@menaceqp", telegramChannel: "@LoveHost_bot" },
        { name: "MezoHost", telegramUser: "@MezoHost", telegramChannel: null }
      ]
    },
    {
      name: "CC & BOÎTE REZ",
      icon: "📮",
      quota: "1/2",
      sellers: [
        { name: "Shenron", telegramUser: "@ByShenron", telegramChannel: "https://t.me/+vDyemTk7sP9hYWI8" }
      ]
    },
    {
      name: "EXCHANGE & VCC",
      icon: "💱",
      quota: "3/3",
      sellers: [
        { name: "Meyer Convert", telegramUser: "@meyerconvert", telegramChannel: "@meyercanal" },
        { name: "KrypTel", telegramUser: "@KrypTel", telegramChannel: "https://t.me/+IE1Hu7R3iPxmYWE0" },
        { name: "Rapidox", telegramUser: "@Rapidoxsecours2", telegramChannel: null }
      ]
    },
    {
      name: "LOG CRYPTO & NEO",
      icon: "📱",
      quota: "1/2",
      sellers: [
        { name: "Zeno", telegramUser: "@rfzenoo", telegramChannel: "@EasyXRP" }
      ]
    }
  ];

  const formatTelegramLink = (username) => {
    if (!username) return null;
    const formattedUsername = username.startsWith('@') ? username.substring(1) : username;
    return `https://t.me/${formattedUsername}`;
  };

  const formatChannelLink = (channel) => {
    if (!channel) return null;
    if (channel.startsWith('https://')) return channel;
    if (channel.startsWith('@')) return formatTelegramLink(channel);
    if (channel.includes('.')) return `https://${channel}`;
    return formatTelegramLink(channel);
  };
  
  const toggleCategory = (index) => {
    setOpenCategories(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    
    // Si recherche non vide, ouvrir toutes les catégories contenant le terme recherché
    if (e.target.value) {
      const newOpenCategories = {};
      
      categories.forEach((category, index) => {
        const categoryMatches = category.name.toLowerCase().includes(e.target.value.toLowerCase());
        const sellersMatch = category.sellers.some(seller => 
          seller.name.toLowerCase().includes(e.target.value.toLowerCase()) ||
          (seller.telegramUser && seller.telegramUser.toLowerCase().includes(e.target.value.toLowerCase()))
        );
        
        if (categoryMatches || sellersMatch) {
          newOpenCategories[index] = true;
        }
      });
      
      setOpenCategories(newOpenCategories);
    }
  };
  
  const filteredCategories = searchTerm 
    ? categories.filter((category) => {
        const categoryMatches = category.name.toLowerCase().includes(searchTerm);
        const sellersMatch = category.sellers.some(seller => 
          seller.name.toLowerCase().includes(searchTerm) ||
          (seller.telegramUser && seller.telegramUser.toLowerCase().includes(searchTerm))
        );
        
        return categoryMatches || sellersMatch;
      })
    : categories;

  return (
    <PageContainer>
      <Header>
        <PageTitle>Certifications HolyHubs</PageTitle>
        <PageDescription>
          Notre liste de vendeurs certifiés et vérifiés par l'équipe HolyHubs. 
          Ces professionnels ont démontré leur fiabilité et la qualité de leurs services.
        </PageDescription>
      </Header>
      
      <SearchBox>
        <SearchInput 
          type="text" 
          placeholder="Rechercher un service ou un vendeur..." 
          value={searchTerm}
          onChange={handleSearch}
        />
      </SearchBox>

      {filteredCategories.map((category, index) => (
        <CategorySection key={index} isOpen={openCategories[index]}>
          <CategoryHeader onClick={() => toggleCategory(index)}>
            <CategoryIcon>{category.icon}</CategoryIcon>
            <CategoryTitle>{category.name}</CategoryTitle>
            <CategoryQuota>{category.quota}</CategoryQuota>
            <ChevronIcon isOpen={openCategories[index]}>
              {openCategories[index] ? <FaChevronUp /> : <FaChevronDown />}
            </ChevronIcon>
          </CategoryHeader>
          
          <CategoryContent isOpen={openCategories[index]}>
            <SellersList>
              {category.sellers.length > 0 ? (
                category.sellers.map((seller, sellerIndex) => (
                  <SellerCard key={sellerIndex}>
                    <SellerHeader>
                      <SellerName>{seller.name}</SellerName>
                      <VerifiedBadge title="Vendeur Certifié" />
                    </SellerHeader>
                    
                    <SellerLinks>
                      <TelegramLink 
                        href={formatTelegramLink(seller.telegramUser)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        <FaTelegram />
                        {seller.telegramUser}
                      </TelegramLink>
                      
                      {seller.telegramChannel && (
                        <TelegramLink 
                          href={formatChannelLink(seller.telegramChannel)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <FaExternalLinkAlt />
                          {seller.telegramChannel}
                        </TelegramLink>
                      )}
                    </SellerLinks>
                  </SellerCard>
                ))
              ) : (
                <SellerCard style={{ opacity: 0.6, gridColumn: '1 / -1', textAlign: 'center' }}>
                  <p>Aucun vendeur certifié dans cette catégorie pour le moment</p>
                </SellerCard>
              )}
            </SellersList>
          </CategoryContent>
        </CategorySection>
      ))}

      <p style={{ textAlign: 'center', fontSize: '0.9rem', opacity: 0.7, marginTop: '1.5rem' }}>
        Pour les conditions de certification, veuillez contacter <a href="https://t.me/pagexplore" target="_blank" rel="noopener noreferrer">@pagexplore</a>
      </p>
    </PageContainer>
  );
};

export default CertifiedSellers; 